/**
 * iter 33 — Tag library UI (Phase 1).
 *
 * <TagChip>     — colored pill displaying a single tag.
 * <TagChipList> — inline row of chips with optional "+N more" overflow.
 * <TagPicker>   — multi-select drawer field. Pulls tags from `/api/tags?category=X`,
 *                 lets the user toggle them, and supports inline "+ Create new tag".
 *
 * Tags are scoped per org. The picker is dumb: it owns no network state for the
 * applied tag set — that's passed in via `value` / `onChange` so the parent form
 * can save (`POST /api/{entity_type}/{entity_id}/tags`) at the same time it
 * saves the rest of the record.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, X as XIcon, Tag as TagIcon } from "@phosphor-icons/react";
import { api, formatErr } from "../lib/api";

export function TagChip({ tag, onRemove, dense = false, testid }) {
  const color = tag.color || "#64748b";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border ${dense ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-xs"} font-medium`}
      style={{
        backgroundColor: color + "1A",   // ~10% alpha
        borderColor: color + "55",
        color,
      }}
      data-testid={testid || `tag-chip-${tag.id}`}
    >
      {tag.name}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="hover:opacity-70"
          aria-label={`Remove tag ${tag.name}`}
          data-testid={`tag-chip-remove-${tag.id}`}
        >
          <XIcon size={dense ? 10 : 12} weight="bold" />
        </button>
      )}
    </span>
  );
}

export function TagChipList({ tags = [], max = 3, dense = true }) {
  if (!tags?.length) return <span className="text-muted-foreground text-xs">—</span>;
  const visible = tags.slice(0, max);
  const overflow = tags.length - visible.length;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map(t => <TagChip key={t.id} tag={t} dense={dense} />)}
      {overflow > 0 && (
        <span className="text-[10px] text-muted-foreground font-medium" data-testid="tag-chip-overflow">
          +{overflow} more
        </span>
      )}
    </div>
  );
}

/**
 * TagPicker — multi-select chip-style.
 *
 * Props:
 *   category     "customer" | "vendor"
 *   value        Tag[]                   currently-applied tags (objects)
 *   onChange     (Tag[]) => void         called when user adds/removes
 *   testid       optional string         data-testid prefix
 *   label        optional override       defaults to "TAGS"
 */
export function TagPicker({ category, value = [], onChange, testid = "tag-picker", label = "TAGS" }) {
  const [allTags, setAllTags] = useState(null);   // null = loading
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#64748b");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const wrapperRef = useRef(null);

  const reload = () => {
    api.get(`/tags?category=${category}`).then(r => setAllTags(r.data)).catch(e => setErr(formatErr(e)));
  };
  useEffect(() => { reload(); }, [category]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close picker on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const appliedIds = useMemo(() => new Set(value.map(v => v.id)), [value]);
  const available = (allTags || []).filter(t => !appliedIds.has(t.id));

  const toggleApply = (tag) => {
    if (appliedIds.has(tag.id)) onChange(value.filter(v => v.id !== tag.id));
    else onChange([...value, tag]);
  };

  const createTag = async () => {
    if (!newName.trim()) return;
    setBusy(true); setErr("");
    try {
      const r = await api.post("/tags", { name: newName.trim(), category, color: newColor });
      const fresh = r.data;
      // Add to local list AND auto-apply.
      setAllTags(prev => {
        const exists = (prev || []).some(t => t.id === fresh.id);
        return exists ? prev : [...(prev || []), fresh];
      });
      if (!appliedIds.has(fresh.id)) onChange([...value, fresh]);
      setNewName(""); setCreating(false);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-1" data-testid={testid}>
      {label && <div className="label-xs">{label}</div>}
      <div
        ref={wrapperRef}
        className="surface p-2 min-h-[44px] cursor-text"
        onClick={() => setOpen(true)}
        data-testid={`${testid}-tray`}
      >
        <div className="flex flex-wrap items-center gap-1.5">
          {value.length === 0 && !open && (
            <span className="text-xs text-muted-foreground" data-testid={`${testid}-empty`}>No tags applied — click to pick</span>
          )}
          {value.map(t => (
            <TagChip
              key={t.id}
              tag={t}
              onRemove={() => onChange(value.filter(v => v.id !== t.id))}
            />
          ))}
          {open && (
            <button
              type="button"
              className="btn btn-ghost btn-sm text-xs ml-auto"
              onClick={(e) => { e.stopPropagation(); setCreating(true); }}
              data-testid={`${testid}-create-btn`}
            >
              <Plus size={10} /> NEW TAG
            </button>
          )}
        </div>

        {open && (
          <div className="mt-2 pt-2 border-t border-border" data-testid={`${testid}-menu`}>
            {available.length === 0 && !creating && (
              <div className="text-xs text-muted-foreground px-1 py-1">
                All available tags are applied. Use <strong>+ NEW TAG</strong> to add a custom one.
              </div>
            )}
            <div className="flex flex-wrap gap-1.5">
              {available.map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleApply(t); }}
                  className="appearance-none focus:outline-none"
                  data-testid={`${testid}-apply-${t.id}`}
                  title="Click to apply"
                >
                  <TagChip tag={t} />
                </button>
              ))}
            </div>

            {creating && (
              <div className="mt-2 pt-2 border-t border-border space-y-2" data-testid={`${testid}-create-form`}>
                <div className="flex items-center gap-2">
                  <TagIcon size={14} className="text-muted-foreground" />
                  <input
                    autoFocus
                    type="text"
                    className="input input-sm flex-1"
                    placeholder="Tag name (e.g. high-priority)"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createTag(); } }}
                    data-testid={`${testid}-create-name`}
                  />
                  <input
                    type="color"
                    value={newColor}
                    onChange={(e) => setNewColor(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="h-7 w-8 rounded border border-border cursor-pointer"
                    data-testid={`${testid}-create-color`}
                    title="Pick chip color"
                  />
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={(e) => { e.stopPropagation(); createTag(); }}
                    disabled={busy || !newName.trim()}
                    data-testid={`${testid}-create-save`}
                  >
                    {busy ? "…" : "ADD"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={(e) => { e.stopPropagation(); setCreating(false); setNewName(""); }}
                  >CANCEL</button>
                </div>
                {err && <div className="text-xs text-destructive" data-testid={`${testid}-create-error`}>{err}</div>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
